import {
  Button,
  Input,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from "@ews-admin/global-design-system";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { Modal } from "../../components/molecules/Modal/Modal";
import { Pagination } from "../../components/molecules/Pagination";
import { careTypesApi } from "../../services/care-types.api";
import {
  CareTypeDto,
  CareTypeLabelVM,
  CareTypePayloadVM,
} from "../../types/care-type.definition";

type LangKey = "en" | "fr" | "ar";

const LANGS: { key: LangKey; label: string }[] = [
  { key: "en", label: "English" },
  { key: "fr", label: "Français" },
  { key: "ar", label: "العربية" },
];

function emptyLabel(): CareTypeLabelVM {
  return { en: "", fr: "", ar: "" };
}

export const CareTypeManagement: React.FC = () => {
  const { t } = useTranslation();

  const [careTypes, setCareTypes] = useState<CareTypeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [lang, setLang] = useState<LangKey>("fr");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCareType, setEditingCareType] = useState<CareTypeDto | null>(
    null
  );
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const [code, setCode] = useState("");
  const [label, setLabel] = useState<CareTypeLabelVM>(emptyLabel());
  const [activeLang, setActiveLang] = useState<LangKey>("fr");
  const [isLoadingTranslations, setIsLoadingTranslations] = useState(false);
  const editingIdRef = useRef<number | null>(null);

  const loadCareTypes = (targetLang: LangKey) => {
    setLoading(true);
    careTypesApi
      .getAll(targetLang)
      .then(setCareTypes)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCareTypes(lang);
  }, [lang]);

  const filtered = careTypes.filter(
    ct =>
      ct.code.toLowerCase().includes(search.toLowerCase()) ||
      ct.label.toLowerCase().includes(search.toLowerCase())
  );

  const totalItems = filtered.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const openCreateModal = () => {
    setEditingCareType(null);
    setCode("");
    setLabel(emptyLabel());
    setActiveLang("fr");
    setIsFormOpen(true);
  };

  const openEditModal = (careType: CareTypeDto) => {
    editingIdRef.current = careType.id;
    setEditingCareType(careType);
    setCode(careType.code);
    setLabel({ en: careType.label, fr: careType.label, ar: careType.label });
    setActiveLang("fr");
    setIsFormOpen(true);

    const requestedId = careType.id;
    setIsLoadingTranslations(true);
    careTypesApi
      .getTranslationsAsLabelVM(requestedId)
      .then(translations => {
        if (editingIdRef.current === requestedId) {
          setLabel(translations);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (editingIdRef.current === requestedId) {
          setIsLoadingTranslations(false);
        }
      });
  };

  const closeFormModal = () => {
    editingIdRef.current = null;
    setIsFormOpen(false);
    setEditingCareType(null);
  };

  const handleSave = async () => {
    if (!code.trim()) return;
    setSaving(true);
    const payload: CareTypePayloadVM = {
      code: code.trim().toUpperCase(),
      label,
    };
    try {
      if (editingCareType) {
        await careTypesApi.update(editingCareType.id, payload);
      } else {
        await careTypesApi.create(payload);
      }
      closeFormModal();
      loadCareTypes(lang);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (confirmDeleteId === null) return;
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    setDeletingId(id);
    try {
      await careTypesApi.delete(id);
      loadCareTypes(lang);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex justify-between items-center page-header">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">
            {t("admin.careTypeMgmt.title")}
          </h2>
          <p className="text-neutral-600">{t("admin.careTypeMgmt.subtitle")}</p>
        </div>
        <div className="flex items-center space-x-2">
          {/* Language selector */}
          <div className="flex rounded-lg border border-neutral-200 overflow-hidden">
            {LANGS.map(({ key, label: langLabel }) => (
              <button
                key={key}
                onClick={() => {
                  setLang(key);
                  setCurrentPage(1);
                }}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  lang === key
                    ? "bg-ews-primary text-white"
                    : "bg-white text-neutral-700 hover:bg-neutral-50"
                }`}
              >
                {langLabel}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={() => loadCareTypes(lang)}
            disabled={loading}
            className="flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span>{t("common.refresh")}</span>
          </Button>
          <Button
            onClick={openCreateModal}
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t("admin.careTypeMgmt.addNew")}</span>
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="page-section pb-0">
        <div className="relative max-w-sm">
          <Input
            size="sm"
            placeholder={t("admin.careTypeMgmt.search")}
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
          {search && (
            <button
              onClick={() => {
                setSearch("");
                setCurrentPage(1);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="page-section">
        {loading ? (
          <div className="py-12 text-center">
            <RefreshCw className="mx-auto w-8 h-8 animate-spin text-neutral-400" />
            <p className="mt-2 text-sm text-neutral-500">
              {t("common.loading")}
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center">
            <h3 className="mt-2 text-sm font-medium text-neutral-900">
              {search
                ? t("admin.careTypeMgmt.noResults")
                : t("admin.careTypeMgmt.empty")}
            </h3>
            {!search && (
              <div className="mt-6">
                <Button onClick={openCreateModal}>
                  <Plus className="mr-2 w-4 h-4" />
                  {t("admin.careTypeMgmt.addNew")}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-neutral-200">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-neutral-600 w-16">
                      {t("admin.careTypeMgmt.table.id")}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-neutral-600 w-64">
                      {t("admin.careTypeMgmt.table.code")}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-neutral-600">
                      {t("admin.careTypeMgmt.table.label")}
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-neutral-600 w-28">
                      {t("common.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {paginated.map(careType => (
                    <tr
                      key={careType.id}
                      className="hover:bg-neutral-50 transition-colors"
                    >
                      <td className="px-4 py-3 text-neutral-500 font-mono text-xs">
                        #{careType.id}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">
                          {careType.code}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-neutral-700">
                        {careType.label || (
                          <span className="italic text-neutral-400">
                            {t("admin.careTypeMgmt.noLabel")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end items-center space-x-1">
                          <button
                            onClick={() => openEditModal(careType)}
                            className="p-1.5 rounded text-neutral-600 hover:bg-neutral-100 hover:text-ews-primary transition-colors"
                            title={t("common.edit")}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(careType.id)}
                            disabled={deletingId === careType.id}
                            className="p-1.5 rounded text-neutral-600 hover:bg-danger-50 hover:text-danger-600 transition-colors disabled:opacity-50"
                            title={t("common.delete")}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-2 text-xs text-neutral-500 border-t border-neutral-100 bg-neutral-50">
                {t("admin.careTypeMgmt.count", { count: totalItems })}
              </div>
            </div>
            {totalPages > 1 && (
              <div className="mt-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={p => setCurrentPage(p)}
                  onItemsPerPageChange={s => {
                    setItemsPerPage(s);
                    setCurrentPage(1);
                  }}
                  showPageNumbers
                  maxVisiblePages={5}
                  showItemsPerPageSelector
                  itemsPerPageOptions={[10, 25, 50, 100]}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Create / Edit Modal */}
      <Modal
        isOpen={isFormOpen}
        onClose={closeFormModal}
        size="md"
        title={
          editingCareType
            ? t("admin.careTypeMgmt.editTitle")
            : t("admin.careTypeMgmt.addNew")
        }
      >
        <div className="p-6 space-y-5">
          {/* Code */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              {t("admin.careTypeMgmt.form.code")}
            </label>
            <Input
              size="sm"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder={t("admin.careTypeMgmt.form.codePlaceholder")}
            />
            <p className="mt-1 text-xs text-neutral-400">
              {t("admin.careTypeMgmt.form.codeHint")}
            </p>
          </div>

          {/* Language tabs for label */}
          <div>
            <div className="flex items-center border-b border-neutral-200 mb-4">
              {LANGS.map(({ key, label: langLabel }) => (
                <button
                  key={key}
                  onClick={() => setActiveLang(key)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                    activeLang === key
                      ? "border-ews-primary text-ews-primary"
                      : "border-transparent text-neutral-600 hover:text-neutral-900"
                  }`}
                >
                  {langLabel}
                </button>
              ))}
              {isLoadingTranslations && (
                <span className="ml-auto flex items-center gap-1 text-xs text-neutral-400 pb-1">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  {t("common.loading")}
                </span>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                {t("admin.careTypeMgmt.form.label")}
              </label>
              <Input
                size="sm"
                value={label[activeLang]}
                onChange={e =>
                  setLabel(prev => ({ ...prev, [activeLang]: e.target.value }))
                }
                placeholder={t("admin.careTypeMgmt.form.labelPlaceholder")}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-2 border-t border-neutral-200">
            <Button
              variant="outline"
              onClick={closeFormModal}
              disabled={saving}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSave}
              loading={saving}
              disabled={saving || !code.trim()}
            >
              {t("common.save")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title={t("admin.careTypeMgmt.deleteConfirm.title")}
        message={t("admin.careTypeMgmt.deleteConfirm.message")}
        variant="danger"
        isLoading={deletingId !== null}
        confirmLabel={t("common.delete")}
      />
    </div>
  );
};
